import { format } from "@cloudinary/url-gen/actions/delivery";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import { useMemo, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import GalleryModal from "@/components/gallery-modal";
import useCloudinary from "@/hooks/use-cloudinary";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";

import type { ReviewImageType } from "../../types";

interface ThumbnailProps {
  children?: React.ReactNode;
  imageId: string;
  onClick(): void;
}

function Thumbnail({ children, imageId, onClick }: ThumbnailProps) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: its fine
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
    <li
      className="relative z-1 aspect-square w-full cursor-pointer overflow-hidden rounded-[10px] shadow-0-0-20 shadow-black/30"
      onClick={onClick}
    >
      <CloudinaryImage
        alt={imageId}
        className="size-full"
        imageId={imageId}
        rW={120}
      />
      {children}
    </li>
  );
}

interface ImagesProps {
  images: ReviewImageType[];
}

export default function Images({ images }: ImagesProps) {
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const isDesktop = useIsBreakPoint("md");
  const thumbnailCount = useMemo(() => (isDesktop ? 7 : 5), [isDesktop]);
  const cloudinary = useCloudinary();

  const handleClick = (index: number) => () => {
    setGalleryIndex(index);
    setIsOpen(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cloudinary.image is "static"
  const photos = useMemo(
    () =>
      images.map(({ imageId }) =>
        cloudinary.image(imageId).delivery(format(auto())).toURL()
      ),
    [images]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleClick is "static"
  const thumbnails = useMemo(() => {
    const lastIndex = (thumbnailCount || 0) - 1;
    const thumbnailImages = images.slice(0, thumbnailCount);
    const moreCount = images.length - thumbnailImages.length;

    return thumbnailImages.map((image, index) => (
      <Thumbnail
        imageId={image.imageId || ""}
        key={image.id}
        onClick={handleClick(index)}
      >
        {index === lastIndex && moreCount > 0 && (
          <p className="absolute inset-0 flex items-center justify-center bg-black/50 font-semibold text-14 text-shadow-0-4-4 text-shadow-black/60 text-white md:text-16">
            +{moreCount}
          </p>
        )}
      </Thumbnail>
    ));
  }, [images, thumbnailCount]);

  return (
    <>
      <ul className="mt-3 grid grid-cols-5 gap-1.5 md:grid-cols-7 md:gap-2">
        {thumbnails}
      </ul>
      {isOpen && (
        <GalleryModal
          onOpenChange={setIsOpen}
          open={isOpen}
          photos={photos}
          startIndex={galleryIndex}
        />
      )}
    </>
  );
}
